import { useMemo } from 'react';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, TrashIcon, CheckIcon, XIcon } from '@/src/ui';
import { Constants, ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';
import type { DistrictBaseDTO } from '../models/districtDTOs';

interface DistrictsTableProps {
    data: Array<DistrictBaseDTO>;
    onEdit: (row: DistrictBaseDTO) => void;
    onDelete: (row: DistrictBaseDTO) => void;
}

export default function DistrictsTable(props: DistrictsTableProps) {
    const { data, onEdit, onDelete } = props;
    const { user } = useAuth();

    const columns = useMemo<TableColumn[]>(() => {
        if (!data || data.length === 0) return [];
        const cols: TableColumn[] = [
            {
                dataKey: 'id',
                header: 'ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.id ?? '-',
            },
            {
                dataKey: 'name',
                header: 'District Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.name,
            },
            {
                dataKey: 'code',
                header: 'Code',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.code,
            },
            {
                dataKey: 'stateCode',
                header: 'State Code',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.stateCode,
            },
            {
                dataKey: 'ssoDomain',
                header: 'SSO Domain',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.ssoDomain,
            },
            {
                dataKey: 'ssoProviderMasterId',
                header: 'SSO ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => row.ssoProviderMasterId,
            },
            {
                dataKey: 'isQADistrict',
                header: 'QA District',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) => (row.isQADistrict ? <CheckIcon /> : <XIcon />),
            },
            {
                dataKey: 'isPracticeAllowed',
                header: 'Practice Allowed',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) =>
                    row.isPracticeAllowed ? <CheckIcon /> : <XIcon />,
            },
            {
                dataKey: 'isPracticeChatAllowed',
                header: 'Practice Chat Allowed',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: DistrictBaseDTO) =>
                    row.isPracticeChatAllowed ? <CheckIcon /> : <XIcon />,
            },
        ];
        cols.push({
            dataKey: 'actions',
            header: 'Actions',
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {onEdit && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Edit" arrow>
                            <button
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => onEdit(row)}
                            >
                                <span role="img" aria-label="edit">
                                    <EditIcon />
                                </span>
                            </button>
                        </Tooltip>
                    )}
                    {onDelete && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Delete" arrow>
                            <button
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => onDelete(row)}
                            >
                                <span role="img" aria-label="delete">
                                    <TrashIcon />
                                </span>
                            </button>
                        </Tooltip>
                    )}
                </div>
            ),
        });
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, onEdit, onDelete]);

    return (
        <DataTable
            data={data}
            columns={columns}
            tableName="Districts"
            rowsPerPageInitial={20}
            emptyMessage="No districts found."
            alternatingRowColor
            defaultSortColumn="name"
            defaultSortDirection="asc"
        />
    );
}
